class StreamRequestQueue {
  constructor() {
    this.queue = new Map(); // 使用 Map 存储请求，key 为请求的唯一标识
    this.activeRequests = new Set(); // 正在处理的请求集合
  }

  /**
   * 添加请求到队列
   * @param {string} key 请求的唯一标识
   * @param {Function} requestFn 返回 Promise 的请求函数
   * @returns {Promise} 返回请求的 Promise
   */
  add(key, requestFn) {
    // 如果已存在相同 key 的请求，直接返回现有的 Promise
    if (this.queue.has(key)) {
      return this.queue.get(key).promise;
    }

    // 创建一个新的队列项
    const queueItem = {
      promise: null,
      cancel: null,
    };

    // 创建可取消的 Promise
    queueItem.promise = new Promise((resolve, reject) => {
      const abortController = new AbortController();

      // 包装取消函数
      queueItem.cancel = (reason) => {
        abortController.abort(reason || 'Request cancelled');
        this._remove(key);
        reject(new Error(reason || 'Request cancelled'));
      };

      // 添加到队列
      this.queue.set(key, queueItem);
      this.activeRequests.add(key);

      // 执行请求函数
      requestFn(abortController.signal)
        .then((result) => {
          this._remove(key);
          resolve(result);
        })
        .catch((error) => {
          this._remove(key);
          reject(error);
        });
    });

    return queueItem.promise;
  }

  /**
   * 从队列中移除请求
   * @param {string} key 请求的唯一标识
   */
  _remove(key) {
    this.queue.delete(key);
    this.activeRequests.delete(key);
  }

  /**
   * 取消指定请求
   * @param {string} key 请求的唯一标识
   * @param {string} reason 取消原因
   */
  cancel(key, reason) {
    if (this.queue.has(key)) {
      this.queue.get(key).cancel(reason);
    }
  }

  /**
   * 检查是否有指定 key 的请求
   * @param {string} key 请求的唯一标识
   * @returns {boolean}
   */
  has(key) {
    return this.queue.has(key);
  }

  /**
   * 获取队列大小
   * @returns {number}
   */
  size() {
    return this.queue.size;
  }

  /**
   * 清空队列并取消所有请求
   * @param {string} reason 取消原因
   */
  clear(reason) {
    for (const [key] of this.queue) {
      this.cancel(key, reason);
    }
  }
}

// 使用示例
export const requestQueue = new StreamRequestQueue();